import React, { useState, useEffect, useCallback } from 'react';
import styles from './FilterBar.module.css';

// Debounce helper
const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterConfig {
  key: string;
  label: string; // Placeholder/Label for the select (e.g. "All Months")
  options: FilterOption[];
}

interface FilterBarProps {
  filters: FilterConfig[];
  onFilterChange: (filters: Record<string, string>) => void;
  onSearchChange?: (term: string) => void;
  searchPlaceholder?: string;
  showExport?: boolean;
  onExport?: () => void;
  className?: string;
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFilterChange,
  onSearchChange,
  searchPlaceholder = 'Search...',
  showExport = false,
  onExport,
  className,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  // Initialize filters with 'all' or empty
  useEffect(() => {
    const initialFilters: Record<string, string> = {};
    filters.forEach((f) => {
      initialFilters[f.key] = 'all';
    });
    setActiveFilters(initialFilters);
  }, [filters]); // Only re-run if filter config changes (e.g. tab switch)

  // Debounced search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      if (onSearchChange) onSearchChange(term);
    }, 300),
    [onSearchChange]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    debouncedSearch(term);
  };

  const handleFilterSelect = (key: string, value: string) => {
    const newFilters = { ...activeFilters, [key]: value };
    setActiveFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: Record<string, string> = {};
    filters.forEach((f) => {
      clearedFilters[f.key] = 'all';
    });
    setActiveFilters(clearedFilters);
    setSearchTerm('');
    onFilterChange(clearedFilters);
    if (onSearchChange) onSearchChange('');
  };

  return (
    <div className={`${styles.filterBar} ${className || ''}`}>
      <div className={styles.topRow}>
        {onSearchChange && (
          <div className={styles.searchSection}>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearch}
              className={styles.searchInput}
              data-testid="search-input"
            />
          </div>
        )}

        <div className={styles.actionsSection}>
          <button onClick={handleClearFilters} className={styles.clearButton} data-testid="clear-filters-button">
            Clear Filters
          </button>
          {showExport && onExport && (
            <button onClick={onExport} className={styles.exportButton} data-testid="export-button">
              <span>Export CSV</span>
            </button>
          )}
        </div>
      </div>

      {filters.length > 0 && (
        <div className={styles.filtersRow}>
          {filters.map((filter) => (
            <select
              key={filter.key}
              value={activeFilters[filter.key] || 'all'}
              onChange={(e) => handleFilterSelect(filter.key, e.target.value)}
              className={styles.filterSelect}
              data-testid={`filter-select-${filter.key}`}
            >
              <option value="all">{filter.label}</option>
              {filter.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
